/**
 * Manages user layout preferences that persist across sessions via localStorage.
 * These preferences are global to the user, not scoped to specific worksheets.
 */
import _ from 'lodash';
import { useEffect, useState } from 'react';

export interface LayoutPreferences {
  sidePanelCollapsed: boolean;
  toolsTabVisible: boolean;
  calculationHierarchyInTab: boolean;
  formulaToolSize: { width: number; height: number };
  /** Share of the formula splitter parent for the variable list (0 = unset). */
  formulaVariableListFlex: number;
}

const STORAGE_KEY = 'sqLayoutPreferences';

type LayoutPreferencesListener = (preferences: LayoutPreferences) => void;
type LayoutPreferencesUpdates = Partial<LayoutPreferences>;
type LayoutPreferencesUpdater =
  | LayoutPreferencesUpdates
  | ((currentPreferences: LayoutPreferences) => LayoutPreferencesUpdates);

const listeners = new Set<LayoutPreferencesListener>();

function getDefaultPreferences(): LayoutPreferences {
  return {
    sidePanelCollapsed: false,
    toolsTabVisible: false,
    calculationHierarchyInTab: false,
    formulaToolSize: { width: 0, height: 0 },
    formulaVariableListFlex: 0,
  };
}

function mergePreferences(currentPreferences: LayoutPreferences, updates: LayoutPreferencesUpdates): LayoutPreferences {
  return {
    ...currentPreferences,
    ...updates,
    formulaToolSize: updates.formulaToolSize
      ? {
          ...currentPreferences.formulaToolSize,
          ...updates.formulaToolSize,
        }
      : currentPreferences.formulaToolSize,
  };
}

function readStoredPreferences(): LayoutPreferencesUpdates {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as LayoutPreferencesUpdates) : {};
  } catch {
    return {};
  }
}

function readPreferences(): LayoutPreferences {
  return mergePreferences(getDefaultPreferences(), readStoredPreferences());
}

function subscribeToLayoutPreferences(listener: LayoutPreferencesListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(preferences: LayoutPreferences) {
  listeners.forEach((listener) => listener(preferences));
}

function persistPreferences(preferences: LayoutPreferences): LayoutPreferences {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    }
  } catch {
    // Ignore localStorage failures
  }

  notifyListeners(preferences);
  return preferences;
}

function updatePreferences(updater: LayoutPreferencesUpdater): LayoutPreferences {
  const currentPreferences = readPreferences();
  const updates = typeof updater === 'function' ? updater(currentPreferences) : updater;
  const nextPreferences = mergePreferences(currentPreferences, updates);

  if (_.isEqual(currentPreferences, nextPreferences)) {
    return currentPreferences;
  }

  return persistPreferences(nextPreferences);
}

export const useLayoutPreferences = () => {
  const [currentPreferences, setCurrentPreferencesState] = useState<LayoutPreferences>(() => readPreferences());

  useEffect(() => {
    const updateFromPreferences = (preferences: LayoutPreferences) => {
      setCurrentPreferencesState((previousPreferences) =>
        _.isEqual(previousPreferences, preferences) ? previousPreferences : preferences,
      );
    };

    const unsubscribe = subscribeToLayoutPreferences(updateFromPreferences);
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== STORAGE_KEY) {
        return;
      }

      updateFromPreferences(readPreferences());
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setCurrentPreferences = (updater: LayoutPreferencesUpdater) => updatePreferences(updater);

  return {
    currentPreferences,
    setCurrentPreferences,
    sidePanelCollapsed: currentPreferences.sidePanelCollapsed,
    setSidePanelCollapsed,
    toolsTabVisible: currentPreferences.toolsTabVisible,
    setToolsTabVisible,
    calculationHierarchyInTab: currentPreferences.calculationHierarchyInTab,
    setCalculationHierarchyInTab,
    formulaToolSize: currentPreferences.formulaToolSize,
    setFormulaToolSize,
    formulaVariableListFlex: currentPreferences.formulaVariableListFlex,
    setFormulaVariableListFlex,
  };
};

export function setSidePanelCollapsed(collapsed: boolean): void {
  updatePreferences({ sidePanelCollapsed: collapsed });
}

export function setToolsTabVisible(visible: boolean): void {
  updatePreferences({ toolsTabVisible: visible });
}

export function setCalculationHierarchyInTab(inTab: boolean): void {
  updatePreferences({ calculationHierarchyInTab: inTab });
}

export function setFormulaToolSize(size: { width: number; height: number }): void {
  updatePreferences({ formulaToolSize: size });
}

export function setFormulaVariableListFlex(flex: number): void {
  if (flex <= 0 || flex >= 1) {
    return;
  }

  updatePreferences({ formulaVariableListFlex: flex });
}

/**
 * @deprecated Only used while upgrading legacy workstep state into localStorage-backed layout preferences.
 */
export function migrateLegacyLayoutPreferences(legacyPreferences: LayoutPreferencesUpdates): void {
  const storedPreferences = readStoredPreferences();
  const updates: LayoutPreferencesUpdates = {};

  if (storedPreferences.sidePanelCollapsed === undefined && legacyPreferences.sidePanelCollapsed !== undefined) {
    updates.sidePanelCollapsed = legacyPreferences.sidePanelCollapsed;
  }

  if (storedPreferences.toolsTabVisible === undefined && legacyPreferences.toolsTabVisible !== undefined) {
    updates.toolsTabVisible = legacyPreferences.toolsTabVisible;
  }

  if (
    storedPreferences.calculationHierarchyInTab === undefined &&
    legacyPreferences.calculationHierarchyInTab !== undefined
  ) {
    updates.calculationHierarchyInTab = legacyPreferences.calculationHierarchyInTab;
  }

  if (storedPreferences.formulaToolSize === undefined && legacyPreferences.formulaToolSize !== undefined) {
    updates.formulaToolSize = legacyPreferences.formulaToolSize;
  }

  if (!_.isEmpty(updates)) {
    updatePreferences(updates);
  }
}

export const exposedForTesting = {
  getCurrentPreferences() {
    return readPreferences();
  },
  resetState() {
    listeners.clear();
  },
};
